import { useState, useCallback, useEffect, useRef } from 'react';
import type { KanbanQuadro } from '@pieltec/contracts';
import { apiFetch } from '../../../lib/csrf-fetch';
import { mensagemErroCarga } from '../../../lib/kanban/erros';
import { criarSequenciador } from '../../../lib/kanban/sequencia';

export function useKanbanQuadro() {
  const [quadro, setQuadro] = useState<KanbanQuadro | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [concluidoDias, setConcluidoDias] = useState<number | 'todos'>(7);
  // WR-WEB-03: só a carga mais recente vale; a anterior é abortada e sua resposta descartada.
  const sequenciador = useRef(criarSequenciador());
  const abortRef = useRef<AbortController | null>(null);
  // Incrementa a cada quadro vindo do servidor (permite saber se algo mais novo chegou durante um gesto).
  const geracaoRef = useRef(0);

  const carregar = useCallback(async () => {
    const id = sequenciador.current.proxima();
    abortRef.current?.abort();
    const controle = new AbortController();
    abortRef.current = controle;
    setLoading(true);
    setErro(null);
    try {
      const resposta = await apiFetch<KanbanQuadro>(`/api/v1/kanban/quadro?concluidoDias=${concluidoDias}`, {
        method: 'GET',
        signal: controle.signal,
      });
      if (!sequenciador.current.ehAtual(id)) return;
      geracaoRef.current += 1;
      setQuadro(resposta);
    } catch (e) {
      if (!sequenciador.current.ehAtual(id)) return;
      setErro(mensagemErroCarga(e));
    } finally {
      if (sequenciador.current.ehAtual(id)) setLoading(false);
    }
  }, [concluidoDias]);

  // Carrega automaticamente quando concluidoDias mudar
  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      sequenciador.current.proxima();
    },
    [],
  );

  return { quadro, setQuadro, loading, erro, carregar, concluidoDias, setConcluidoDias, geracaoRef };
}
